import React, { useState } from 'react';
import { Table } from './Table';
import { SAIFileResponse } from '../services/api';

interface Column {
  key: string;
  header: string;
  render?: (item: any) => React.ReactNode;
}

interface TrainedModel {
  id: number;
  name: string;
  data_points: number;
  key_metrics: string;
  remarks: string;
  algorithm: string;
  status: string;
  created: string;
  modified: string;
  train_date: string;
}

interface ExistingModelSelectionProps {
  selectedSAIFile: SAIFileResponse;
  onBack: () => void;
  onNext: (modelId: string, modelName: string) => void;
}

export const ExistingModelSelection: React.FC<ExistingModelSelectionProps> = ({
  selectedSAIFile,
  onBack,
  onNext,
}) => {
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);

  // Get trained models from the selected SAI file
  const trainedModels: TrainedModel[] = selectedSAIFile.trained_models || [];

  const columns: Column[] = [
    {
      key: 'name',
      header: 'Model',
    },
    {
      key: 'key_metrics',
      header: 'Key Metrics',
      render: (item: TrainedModel) => {
        const html = item.key_metrics;
        if (html == null || html === '') return null;
        return <span dangerouslySetInnerHTML={{ __html: html }} />;
      },
    },
    {
      key: 'remarks',
      header: 'Remarks',
    },
    {
      key: 'algorithm',
      header: 'ML Algorithm',
      render: (item: TrainedModel) => {
        // Format algorithm name for display (title case)
        const algorithmNames: Record<string, string> = {
          'random_forest': 'Random Forest',
          'support_vector_machine': 'Support Vector Machine',
          'neural_network': 'Neural Network',
          'gradient_boosting': 'Gradient Boosting',
        };
        const display =
          algorithmNames[item.algorithm] ||
          item.algorithm.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        return display;
      },
    },
    {
      key: 'status',
      header: 'Training',
      render: (item: TrainedModel) => {
        // Capitalize status for display
        const statusDisplay = item.status.charAt(0).toUpperCase() + item.status.slice(1);
        return (
          <span
            className={`training-status training-status-${item.status.toLowerCase()}`}
          >
            {statusDisplay}
          </span>
        );
      },
    },
    {
      key: 'train_date',
      header: 'Model Training Date',
      render: (item: TrainedModel) => new Date(item.train_date).toLocaleString(),
    },
  ];

  const handleModelSelect = (id: string) => {
    setSelectedModelId(id);
    // Automatically navigate to parameters when a row is clicked
    const selectedModel = trainedModels.find(m => m.id.toString() === id.toString());
    if (selectedModel) {
      onNext(id, selectedModel.name);
    }
  };

  return (
    <div className="wizard-step-container">
      <div className="wizard-step-header">
        <button className="back-button" onClick={onBack}>
          <span className="back-arrow">←</span> Back
        </button>
        <h1>Select a Model</h1>
      </div>

      <div className="existing-model-table-container">
        <Table
          items={trainedModels}
          columns={columns}
          selectedItemId={selectedModelId}
          onItemSelect={handleModelSelect}
          scrollable={true}
          maxItemsPerPage={10}
        />
      </div>
    </div>
  );
};

